import { supabase } from '../lib/supabase'
import { supabaseAdmin } from '../lib/supabaseAdmin'
import { notificationService } from './notificationService'

export interface SystemNotification {
  id: string
  title: string
  message: string
  type: 'info' | 'warning' | 'error' | 'success'
  priority: 'low' | 'medium' | 'high' | 'critical'
  targetAudience: 'all' | 'admins' | 'users' | 'specific'
  isActive: boolean
  createdAt: string
  expiresAt?: string
  createdBy: string
  specificUserIds?: string[]
}

export interface CreateSystemNotificationData {
  title: string
  message: string
  type: 'info' | 'warning' | 'error' | 'success'
  priority: 'low' | 'medium' | 'high' | 'critical'
  targetAudience: 'all' | 'admins' | 'users' | 'specific'
  expiresAt?: string
  specificUserIds?: string[]
  createdBy: string
}

class SystemNotificationService {
  // Create a system notification and send to target users
  async createSystemNotification(data: CreateSystemNotificationData): Promise<{ success: boolean; notification?: SystemNotification; error?: string }> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      // First, create the system notification record
      const systemNotificationData = {
        title: data.title,
        message: data.message,
        type: data.type,
        priority: data.priority,
        target_audience: data.targetAudience,
        is_active: true,
        created_at: new Date().toISOString(),
        expires_at: data.expiresAt || null,
        created_by: data.createdBy,
        specific_user_ids: data.specificUserIds || null
      }

      const { data: systemNotification, error: systemError } = await supabase
        .from('system_notifications')
        .insert(systemNotificationData)
        .select()
        .single()

      if (systemError) throw systemError

      // Now send individual notifications to target users
      console.log('🎯 Getting target users for audience:', data.targetAudience)
      const targetUsers = await this.getTargetUsers(data.targetAudience, data.specificUserIds)
      
      console.log(`📊 Found ${targetUsers.length} target users for notification:`, targetUsers)
      
      if (targetUsers.length > 0) {
        console.log('📤 Sending notifications to users:', targetUsers)
        await this.sendNotificationsToUsers(targetUsers, {
          title: data.title,
          message: data.message,
          type: data.type,
          metadata: {
            systemNotificationId: systemNotification.id,
            priority: data.priority,
            createdBy: data.createdBy
          }
        })
        console.log('✅ Notifications sent successfully')
      } else {
        console.warn('❌ No target users found for notification! Check user roles and target audience.')
        console.warn('Target audience was:', data.targetAudience)
      }

      const notification: SystemNotification = {
        id: systemNotification.id,
        title: systemNotification.title,
        message: systemNotification.message,
        type: systemNotification.type,
        priority: systemNotification.priority,
        targetAudience: systemNotification.target_audience,
        isActive: systemNotification.is_active,
        createdAt: systemNotification.created_at,
        expiresAt: systemNotification.expires_at,
        createdBy: systemNotification.created_by,
        specificUserIds: systemNotification.specific_user_ids
      }

      return { success: true, notification }
    } catch (error) {
      console.error('Error creating system notification:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' }
    }
  }

  // Get target users based on audience type
  private async getTargetUsers(targetAudience: string, specificUserIds?: string[]): Promise<string[]> {
    try {
      if (!supabaseAdmin) throw new Error('Supabase admin client not configured')

      if (targetAudience === 'specific' && specificUserIds) {
        console.log('Using specific user IDs:', specificUserIds)
        return specificUserIds
      }

      let query = supabaseAdmin.from('profiles').select('id, email, role')

      if (targetAudience === 'admins') {
        query = query.in('role', ['admin', 'super_admin'])
      } else if (targetAudience === 'users') {
        // Target regular users (not admins)
        query = query.or('role.eq.user,role.is.null')
      }
      // For 'all', we don't add any filters

      console.log('🔍 Fetching target users for audience:', targetAudience)
      const { data, error } = await query

      if (error) {
        console.error('❌ Database error getting target users:', error)
        throw error
      }

      console.log('📋 Found target users:', data)
      const userIds = (data || []).map(user => user.id)
      console.log('🆔 Target user IDs:', userIds)
      
      return userIds
    } catch (error) {
      console.error('Error getting target users:', error)
      return []
    }
  }

  // Send notifications to multiple users
  private async sendNotificationsToUsers(userIds: string[], notificationData: {
    title: string
    message: string
    type: 'info' | 'warning' | 'error' | 'success'
    metadata?: Record<string, any>
  }): Promise<void> {
    try {
      console.log(`Sending notifications to ${userIds.length} users`)
      
      // Send notifications to each user
      const promises = userIds.map(async (userId, index) => {
        try {
          console.log(`Sending notification ${index + 1}/${userIds.length} to user:`, userId)
          const result = await notificationService.createNotification({
            userId,
            title: notificationData.title,
            message: notificationData.message,
            type: notificationData.type,
            metadata: notificationData.metadata,
            useAdminClient: true // Use admin client to bypass RLS
          })
          console.log(`Notification sent successfully to user ${userId}`)
          return result
        } catch (error) {
          console.error(`Failed to send notification to user ${userId}:`, error)
          throw error
        }
      })

      const results = await Promise.allSettled(promises)
      
      const successful = results.filter(r => r.status === 'fulfilled').length
      const failed = results.filter(r => r.status === 'rejected').length
      
      console.log(`Notification sending complete: ${successful} successful, ${failed} failed`)
      
      if (failed > 0) {
        console.warn('Some notifications failed to send:', 
          results.filter(r => r.status === 'rejected').map(r => (r as PromiseRejectedResult).reason)
        )
      }
    } catch (error) {
      console.error('Error sending notifications to users:', error)
      throw error
    }
  }

  // Get all system notifications
  async getSystemNotifications(): Promise<{ success: boolean; notifications?: SystemNotification[]; error?: string }> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      const { data, error } = await supabase
        .from('system_notifications')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error

      const notifications: SystemNotification[] = (data || []).map(n => ({
        id: n.id,
        title: n.title,
        message: n.message,
        type: n.type,
        priority: n.priority,
        targetAudience: n.target_audience,
        isActive: n.is_active,
        createdAt: n.created_at,
        expiresAt: n.expires_at,
        createdBy: n.created_by,
        specificUserIds: n.specific_user_ids
      }))

      return { success: true, notifications }
    } catch (error) {
      console.error('Error fetching system notifications:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' }
    }
  }

  // Toggle system notification active status
  async toggleSystemNotification(id: string, isActive: boolean): Promise<{ success: boolean; error?: string }> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      const { error } = await supabase
        .from('system_notifications')
        .update({ 
          is_active: isActive,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)

      if (error) throw error

      return { success: true }
    } catch (error) {
      console.error('Error toggling system notification:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' }
    }
  }

  // Delete system notification
  async deleteSystemNotification(id: string): Promise<{ success: boolean; error?: string }> {
    try {
      if (!supabase) throw new Error('Supabase not configured')

      const { error } = await supabase
        .from('system_notifications')
        .delete()
        .eq('id', id)

      if (error) throw error

      return { success: true }
    } catch (error) {
      console.error('Error deleting system notification:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' }
    }
  }

  // Send notification to all users (for maintenance, etc.)
  async sendMaintenanceNotification(title: string, message: string, scheduledTime?: string): Promise<{ success: boolean; error?: string }> {
    return this.createSystemNotification({
      title,
      message,
      type: 'warning',
      priority: 'high',
      targetAudience: 'all',
      createdBy: 'System',
      expiresAt: scheduledTime
    })
  }

  // Send security alert to admins
  async sendSecurityAlert(title: string, message: string): Promise<{ success: boolean; error?: string }> {
    return this.createSystemNotification({
      title,
      message,
      type: 'error',
      priority: 'critical',
      targetAudience: 'admins',
      createdBy: 'Security System'
    })
  }

  // Send feature announcement to users
  async sendFeatureAnnouncement(title: string, message: string): Promise<{ success: boolean; error?: string }> {
    return this.createSystemNotification({
      title,
      message,
      type: 'success',
      priority: 'medium',
      targetAudience: 'users',
      createdBy: 'Product Team'
    })
  }
}

export const systemNotificationService = new SystemNotificationService() 